/**
 * SONE core — a workspace's defaults for how elements look.
 *
 * The rules for what a theme may say, kept here so the server that validates it
 * and the client that renders it cannot disagree about the answer.
 *
 * ## Steps, not values
 *
 * A size is a step on a scale and not a pixel count; a colour is a palette name
 * and not a hex value. Free numbers produce a heading that no longer relates to
 * the body text underneath it, and the person who set it cannot see that is
 * what happened — the scale exists precisely so those relationships hold. A
 * stored hex would also stop being right the moment somebody switched theme.
 *
 * ## It fills gaps, it does not override
 *
 * A block attribute of `null` means "as the design decides" (ADR-0021), and
 * this is what decides. A block carrying its own value keeps it. Nothing here
 * is written into a document, and a workspace with no theme renders the way
 * every workspace rendered before themes existed.
 */
import { AA, luminance, readableInk, WORST_GROUND } from './contrast.js';
/** The kinds of element a theme can speak about. */
export const THEMED_ELEMENTS = [
    'heading1',
    'heading2',
    'heading3',
    'body',
    'quote',
    'callout',
    'code',
    'list',
];
/**
 * Size steps, relative to the element's own default.
 *
 * Zero is "as it is now", so a theme that sets only a colour leaves every size
 * alone and a theme reset to zero is indistinguishable from no theme. The range
 * is deliberately short: past a few steps a heading stops being a heading and
 * starts being a banner.
 */
export const SIZE_STEPS = [-2, -1, 0, 1, 2, 3];
/** Spacing steps above and below, in the same spirit. */
export const SPACE_STEPS = [0, 1, 2, 3];
/**
 * The palette.
 *
 * The same names as tags, select options and block colours, so a workspace has
 * one vocabulary for colour rather than four.
 */
export const THEME_COLORS = [
    'grey',
    'red',
    'orange',
    'yellow',
    'green',
    'blue',
    'purple',
    'pink',
];
/** Is this one of the eight? */
export const isPaletteName = (value) => inList(THEME_COLORS, value);
/** A colour of one's own. Six digits only: shorthand and alpha are not offered. */
/**
 * What the design makes of each of the eight names (ADR-0136).
 *
 * Here rather than in the settings form, where it used to live, because
 * something now has to *measure* these: an accent stored as a name still needs a
 * contrast colour computed for it, and `var(--sone-palette-yellow)` has no
 * luminance. The stylesheet declares them too — it has to, they are what the
 * browser resolves — and `web`'s own test compares the two lists, which is the
 * arrangement that was already there and is unchanged.
 *
 * Grey is the exception the stylesheet makes: it is defined from the ink ramp
 * rather than as a literal, so the test that compares them skips it.
 */
export const PALETTE_DEFAULTS = {
    grey: '#8a8a8a',
    red: '#d64545',
    orange: '#d97706',
    yellow: '#ca8a04',
    green: '#16a34a',
    blue: '#2563eb',
    purple: '#7c3aed',
    pink: '#db2777',
};
export const isCustomColor = (value) => typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
/** Either, or null for anything else. */
export function readChosenColor(value) {
    if (isPaletteName(value))
        return value;
    if (isCustomColor(value))
        return value.toLowerCase();
    return null;
}
/**
 * What to put in a stylesheet for a chosen colour.
 *
 * A name becomes the workspace's variable, so it keeps following the palette; a
 * literal is itself. Returning undefined for anything else is what lets an
 * older client draw the design's own answer instead of breaking on a value it
 * does not recognise.
 */
export function colorValue(value) {
    if (isPaletteName(value))
        return `var(--sone-palette-${value})`;
    if (isCustomColor(value))
        return value;
    return undefined;
}
/**
 * The pieces of furniture a theme may treat separately (ADR-0122).
 *
 * Asked for as *„einzelne Elemente behandeln, z.B. die neue schmale Leiste
 * dunkel und Rest hell"*, against the complaint that a theme which only
 * recolours everything at once is what every tool already has.
 *
 * The page is not among them. Inverting the reading surface is "dark mode for
 * this workspace", and that is the reader's choice rather than the workspace's
 * — somebody outside in the sun wants light whatever a workspace prefers, which
 * is the argument the stylesheet already makes for `prefers-color-scheme`. The
 * top bar is not among them either: it sits on the page surface on purpose, so
 * the title reads as part of what is under it rather than as a header above it.
 */
export const THEMED_SURFACES = ['rail', 'sidebar', 'panel'];
/**
 * What a surface may be told to be.
 *
 * A **relationship**, never a colour. `#101010` on the rail is black in both
 * schemes, so a workspace that set it would have a black bar against a black
 * page for everybody reading in the dark. `inverted` is the whole argument in
 * one word: light-on-dark for a reader in the light theme and dark-on-light for
 * a reader in the dark one, from one stored value.
 *
 * `follow` is the design's own answer, and it is stored as nothing at all —
 * absent and "as the design decides" are the same state (ADR-0021), and a
 * stored default stops being right the moment the design changes underneath it.
 */
export const SURFACE_TREATMENTS = ['follow', 'raised', 'sunken', 'inverted', 'accent'];
/**
 * How round everything is.
 *
 * Three steps rather than a length, for the reason every other value here is a
 * step: a small control with a large radius reads as a pill by accident, and
 * the person who set it cannot see that is what happened. `soft` is what the
 * design does today and is therefore stored as nothing, the same rule `follow`
 * follows.
 */
export const CORNERS = ['sharp', 'soft', 'round'];
/**
 * Light, dark, or whatever the device says (ADR-0124).
 *
 * **Four states are needed and only three are named here.** `system` is not the
 * absence of a choice: it is the choice to let the device decide, and somebody
 * who makes it is overriding a workspace that says dark. Absence — the field
 * missing — is the other answer: "whatever the level above says". Collapsing
 * the two costs exactly the case this exists for.
 */
export const COLOR_SCHEMES = ['light', 'dark', 'system'];
/**
 * The typefaces a workspace may choose between (ADR-0127).
 *
 * A **named pair** — the face body text is set in and the one code is set in —
 * for the reason every other value here is a name rather than a value: a font
 * family typed into a box is a font somebody's machine may not have, and the
 * person who typed it sees their own machine and cannot tell.
 *
 * A short, closed list on purpose. Every face is a file this instance serves
 * itself (ADR-0068), so an open list is an open-ended download.
 *
 * `designed` is what the design does today and is therefore stored as nothing,
 * the rule `follow` and `soft` already follow.
 */
export const FONT_PAIRS = ['designed', 'reading', 'plain', 'system'];
/**
 * What each pair resolves to.
 *
 * Every stack ends in something the machine already has. A downloaded face can
 * fail — a slow network, a blocked request, a reader who turned webfonts off —
 * and the fallback is what they read in the meantime; a stack that ends in the
 * family name alone ends in whatever the browser decides, which is usually
 * Times.
 */
const PAIRS = {
    reading: {
        text: "'Literata Var', Georgia, 'Times New Roman', serif",
        mono: "'Jetbrains Mono Var', ui-monospace, SFMono-Regular, Menlo, monospace",
    },
    plain: {
        text: "'Inter Var', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
        mono: "'Jetbrains Mono Var', ui-monospace, SFMono-Regular, Menlo, monospace",
    },
    /*
     * The machine's own type, and the only pair that downloads nothing.
     *
     * It names no face this instance ships, which is the whole point: somebody
     * on a metered connection, or an operator who would rather serve no fonts at
     * all, gets an interface with no webfont in it.
     */
    system: {
        text: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
        mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    },
};
const inList = (list, value) => list.includes(value);
/**
 * Keep only what a theme is allowed to say.
 *
 * Unknown elements and unusable values are dropped rather than rejected. A
 * theme arrives from a form, and one stale field should not cost somebody the
 * rest of their settings — the same reasoning as a collection view naming a
 * deleted column.
 *
 * Returns a new object; the input is never modified.
 */
export function sanitiseTheme(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input))
        return {};
    const out = {};
    // The two whole-interface knobs, from the same eight names entries use or a
    // literal — the same rule the palette follows, so somebody who has coloured a
    // folder already knows what is accepted here.
    const tint = input['tint'];
    if (inList(THEME_COLORS, tint) || isCustomColor(tint)) {
        out.tint = isCustomColor(tint) ? tint.toLowerCase() : tint;
    }
    const accent = input['accent'];
    if (inList(THEME_COLORS, accent) || isCustomColor(accent)) {
        out.accent = isCustomColor(accent) ? accent.toLowerCase() : accent;
    }
    const palette = sanitisePalette(input['palette']);
    if (Object.keys(palette).length > 0)
        out.palette = palette;
    const surfaces = sanitiseSurfaces(input['surfaces']);
    if (Object.keys(surfaces).length > 0)
        out.surfaces = surfaces;
    // `soft` is the design's own, so it is dropped along with everything
    // unrecognised: a theme that stored it would keep today's radii after the
    // design had moved on.
    const corners = input['corners'];
    if (corners === 'sharp' || corners === 'round')
        out.corners = corners;
    const scheme = input['scheme'];
    if (inList(COLOR_SCHEMES, scheme))
        out.scheme = scheme;
    const fonts = input['fonts'];
    if (inList(FONT_PAIRS, fonts) && fonts !== 'designed')
        out.fonts = fonts;
    for (const [key, raw] of Object.entries(input)) {
        if (!inList(THEMED_ELEMENTS, key))
            continue;
        if (!raw || typeof raw !== 'object' || Array.isArray(raw))
            continue;
        const entry = raw;
        const element = {};
        if (inList(SIZE_STEPS, entry['size']))
            element.size = entry['size'];
        const color = readChosenColor(entry['color']);
        if (color)
            element.color = color;
        if (inList(SPACE_STEPS, entry['spaceAbove']))
            element.spaceAbove = entry['spaceAbove'];
        if (inList(SPACE_STEPS, entry['spaceBelow']))
            element.spaceBelow = entry['spaceBelow'];
        // An element with nothing usable left is omitted rather than stored empty,
        // so "has a theme" and "has settings" mean the same thing.
        if (Object.keys(element).length > 0)
            out[key] = element;
    }
    return out;
}
/**
 * An instance's theme with a workspace's on top of it (ADR-0123).
 *
 * The same sentence a theme has always been described with, one level up:
 *
 * > It fills gaps, it does not override.
 *
 * The instance says what things look like where nobody has said otherwise, a
 * workspace fills in over it, and a workspace that has never opened its own
 * settings looks exactly like the instance — which is what a base design means.
 *
 * The difficulty is entirely in the settings that are **maps**. A whole-value
 * overwrite would mean a workspace that changed one palette colour discarded
 * every other colour the instance chose, invisibly: its own settings screen
 * would go on showing seven unset names.
 *
 * Both sides are expected to have been through `sanitiseTheme` already — this
 * is not a second door into a theme, and it makes no checks of its own.
 * Neither input is modified; both are held in state by whoever fetched them.
 */
export function mergeThemes(base, over) {
    const merged = { ...base, ...over };
    if (base.palette || over.palette) {
        merged.palette = { ...base.palette, ...over.palette };
    }
    if (base.surfaces || over.surfaces) {
        merged.surfaces = { ...base.surfaces, ...over.surfaces };
    }
    for (const element of THEMED_ELEMENTS) {
        // An element is a map of four properties in the same way: enlarging a
        // heading is not a request to drop the colour the instance gave it.
        if (base[element] && over[element]) {
            merged[element] = { ...base[element], ...over[element] };
        }
    }
    return merged;
}
/**
 * Light or dark, once every level has had its say (ADR-0124).
 *
 * Person, then workspace-over-instance, then the device. One function because
 * there are two callers who must not disagree: the hook that applies it, and
 * the line that applies the remembered answer before the application has
 * loaded — an interface that changes colour a second after it appears is worse
 * than one that was the wrong colour to begin with.
 *
 * An unusable stored value counts as nobody having said. Both arguments come
 * out of storage — a column and a merged theme — and a person whose account
 * holds a word from a future release should get the ordinary answer rather
 * than an interface that cannot decide what colour it is.
 */
export function resolveScheme(
/** What the person chose. Null or absent: as the workspace says. */
personal, 
/** The instance's theme with the workspace's merged over it. */
theme) {
    if (inList(COLOR_SCHEMES, personal))
        return personal;
    if (inList(COLOR_SCHEMES, theme.scheme))
        return theme.scheme;
    return 'system';
}
/**
 * Black or white on this colour, whichever can be read.
 *
 * The sRGB relative luminance from WCAG, and the same threshold every
 * implementation of that formula uses. Not a guess at "is this light": a mid
 * green and a mid blue of the same lightness need different answers, and the
 * formula is why.
 *
 * The arithmetic moved to `contrast.ts` when the token layer needed the same
 * formula (ADR-0135). Two implementations of one formula is two chances to get
 * it wrong, and only one of them would have had a test.
 */
export function readableOn(color) {
    /*
     * 0.179 is where **black** and white are equally readable by the WCAG ratio,
     * and that crossover is what makes this a guarantee rather than a preference:
     * a colour sitting exactly on it is 4.58:1 either way, above AA for body
     * text, and every other colour is further from one end than that.
     *
     * **It returned `#141210`, and the threshold was computed for `#000000`**
     * (ADR-0135). A warm near-black rather than black, which is the right
     * instinct everywhere else in this design and is wrong here: its own
     * luminance moves the crossover to 0.193, and at 0.179 the dark answer is
     * only 4.08:1. `#aa44dd` — an ordinary purple somebody would pick out of a
     * colour input — landed there. The guarantee the comment claimed had never
     * held, by about half a ratio point, since the day it was written.
     *
     * The two ends have to be the extremes or there is no guarantee to make: a
     * dark end above a luminance of 0.0019 cannot reach 4.5:1 at its own
     * crossover, and `--ink-950` is already over that.
     */
    return luminance(color) > 0.179 ? '#000000' : '#ffffff';
}
/** The three surfaces, and only a treatment somebody could have meant. */
function sanitiseSurfaces(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input))
        return {};
    const out = {};
    for (const [name, value] of Object.entries(input)) {
        if (!inList(THEMED_SURFACES, name))
            continue;
        // `follow` falls out here with everything unrecognised, which is the point:
        // it is the absence of a treatment rather than one of them.
        if (!inList(SURFACE_TREATMENTS, value) || value === 'follow')
            continue;
        out[name] = value;
    }
    return out;
}
/**
 * What each treatment resolves to.
 *
 * Every value is an indirection, and that is the half that makes dark mode
 * free: the stylesheet defines each of these twice, once per scheme, so one
 * stored `inverted` reaches opposite ends depending on where the reader is.
 * A literal colour here would be the same colour in both.
 */
const TREATMENTS = {
    raised: {
        bg: 'var(--surface-raised)',
        ink: 'var(--text-primary)',
        muted: 'var(--text-muted)',
        border: 'var(--border-subtle)',
        hover: 'var(--surface-hover)',
        accent: 'var(--accent)',
        'accent-ink': 'var(--accent-contrast)',
        'accent-quiet': 'var(--sone-base-accent-quiet)',
    },
    sunken: {
        bg: 'var(--surface-sunken)',
        ink: 'var(--text-primary)',
        muted: 'var(--text-muted)',
        border: 'var(--border-subtle)',
        hover: 'var(--surface-hover)',
        accent: 'var(--accent)',
        'accent-ink': 'var(--accent-contrast)',
        'accent-quiet': 'var(--sone-base-accent-quiet)',
    },
    inverted: {
        bg: 'var(--sone-inverse-bg)',
        ink: 'var(--sone-inverse-ink)',
        muted: 'var(--sone-inverse-muted)',
        border: 'var(--sone-inverse-border)',
        hover: 'var(--sone-inverse-hover)',
        // The *other* scheme's accent. `--accent-500` on near-black is the same
        // too-dark green the dark theme already replaces with `--accent-300`, and
        // an inverted surface is that theme's ground standing in this one.
        accent: 'var(--sone-inverse-accent)',
        'accent-ink': 'var(--sone-inverse-accent-contrast)',
        'accent-quiet': 'var(--sone-inverse-accent-quiet)',
    },
    accent: {
        bg: 'var(--accent)',
        // Computed, never chosen — the rule the accent itself follows. A pale
        // accent needs dark text on it, and a workspace that could choose would be
        // a workspace that can make a rail nobody can read.
        ink: 'var(--accent-contrast)',
        muted: 'var(--sone-accent-muted)',
        // Its own colour, which is a surface with no line around it: a border in
        // some other colour would draw a frame nobody asked for.
        border: 'var(--accent)',
        hover: 'var(--sone-accent-hover)',
        /*
         * Swapped, because accent on accent is nothing at all.
         *
         * The mark's third bar is `var(--accent)` and a filled button is drawn in
         * it, so on an accent-coloured surface both disappear. The pair trades
         * places: the button becomes the contrast colour with accent-coloured text,
         * which is the same two colours the other way round and readable.
         */
        accent: 'var(--accent-contrast)',
        'accent-ink': 'var(--accent)',
        'accent-quiet': 'var(--sone-accent-hover)',
    },
};
/** Three radii from one choice, because the relationship between them is the design. */
const RADII = {
    sharp: ['0px', '0px', '0px'],
    round: ['4px', '6px', '10px'],
};
/** The eight names, and what this workspace makes of them. */
function sanitisePalette(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input))
        return {};
    const out = {};
    for (const [name, value] of Object.entries(input)) {
        // Only the eight, and only a literal. A name mapped to another name would
        // be an alias — one more thing that can point at itself, for no gain.
        if (!inList(THEME_COLORS, name))
            continue;
        if (isCustomColor(value))
            out[name] = value.toLowerCase();
    }
    return out;
}
/**
 * The custom properties a theme resolves to.
 *
 * Returned as a plain map so the caller decides where to put them; nothing here
 * touches the document. A property is emitted only for a value the theme
 * actually sets, so everything else keeps falling through to the stylesheet's
 * own answer — which is what makes a theme a set of gaps filled rather than a
 * replacement design.
 */
export function themeProperties(theme) {
    const properties = {};
    /*
     * The tint, mixed into the surfaces by the stylesheet rather than here.
     *
     * A single property, because the stylesheet is where the ramp's proportions
     * live: it knows that the sunken surface takes more of the hue than the raised
     * one, and that the dark theme takes less of it than the light one. Emitting
     * eight computed colours from here would move that knowledge somewhere it
     * cannot be read beside the design tokens.
     */
    const tint = colorValue(theme.tint);
    if (tint !== undefined)
        properties['--sone-theme-tint'] = tint;
    const accent = colorValue(theme.accent);
    if (accent !== undefined) {
        properties['--accent'] = accent;
        // Computed, never chosen. A pale accent needs dark text on it and a deep one
        // needs light text, and a workspace that picked the wrong one would have a
        // button nobody can read.
        /*
         * The literal behind whatever was chosen.
         *
         * A name is a name in the browser and a colour here, and it has to be a
         * colour here or nothing can be computed from it. **`readableOn` was being
         * handed `var(--sone-palette-yellow)`**, whose luminance parses as `NaN`,
         * whose comparison against the threshold is false — so every accent stored
         * as a name got white text on it, including yellow, at 3.0:1 (ADR-0136).
         * Silent, because a wrong answer and no answer looked the same.
         *
         * The workspace's own override first, because a workspace that redefined
         * `blue` means its blue.
         */
        const literal = isCustomColor(theme.accent)
            ? theme.accent
            : isPaletteName(theme.accent)
                ? (theme.palette?.[theme.accent] ?? PALETTE_DEFAULTS[theme.accent])
                : undefined;
        if (literal)
            properties['--accent-contrast'] = readableOn(literal);
        /*
         * And the accent as *text*, which is a different question (ADR-0136).
         *
         * `--accent-contrast` is what goes **on** the accent; this is the accent
         * **as** a colour to read — a link, an active icon — on one of the page's
         * own surfaces. One value cannot satisfy both except by luck, and the
         * design already knows it: it gives itself `--accent-500` in light and
         * `--accent-300` in dark, *because the one that works on white glows on
         * black*. A workspace that set an accent got one value for both.
         *
         * Two derivations rather than one, because a stored value cannot know which
         * scheme its reader is in — the same shape `inverted` has (ADR-0122). The
         * stylesheet declares which of the two `--accent-text` means, once per
         * scheme, and one stored accent then reads correctly for both.
         *
         */
        if (literal) {
            properties['--sone-theme-accent-on-light'] = readableInk(literal, WORST_GROUND.light);
            properties['--sone-theme-accent-on-dark'] = readableInk(literal, WORST_GROUND.dark);
            /*
             * And the accent as a *line*, at the floor a line has (ADR-0137).
             *
             * A focus ring, the border of a focused field, the rule beside a selected
             * item: a control's boundary, which the standard asks 3:1 of rather than
             * 4.5:1. Two reasons it is not simply the text derivation.
             *
             * It would be **darker than it needs to be**, and a border drawn beside a
             * fill of the same accent would no longer match it — a ring around every
             * primary button.
             *
             * And it would be **darker than the fill it outlines**, which is the same
             * ring seen from the other side.
             *
             * The floor is lower, so the colour moves less and stays nearer the one
             * the workspace chose, which is the whole point of letting them choose.
             */
            properties['--sone-theme-accent-line-on-light'] = readableInk(literal, WORST_GROUND.light, AA.nonText);
            properties['--sone-theme-accent-line-on-dark'] = readableInk(literal, WORST_GROUND.dark, AA.nonText);
        }
    }
    for (const [name, value] of Object.entries(theme.palette ?? {})) {
        // The same variable the stylesheet defines, overridden for this workspace.
        // Everything that stored the *name* follows without knowing this happened.
        properties[`--sone-palette-${name}`] = value;
    }
    for (const surface of THEMED_SURFACES) {
        const treatment = theme.surfaces?.[surface];
        if (!treatment)
            continue;
        // A surface nobody treated emits nothing, so the stylesheet's own fallback
        // is what draws it — gaps filled, not a replacement design.
        for (const [part, value] of Object.entries(TREATMENTS[treatment])) {
            properties[`--sone-theme-${surface}-${part}`] = value;
        }
    }
    if (theme.fonts) {
        // Both halves, because a pair is two faces: answering only the first is how
        // a workspace ends up with a serif page and the design's own grotesque in
        // every code block — a mismatch nobody chose.
        properties['--sone-font'] = PAIRS[theme.fonts].text;
        properties['--sone-font-mono'] = PAIRS[theme.fonts].mono;
    }
    if (theme.corners) {
        const [sm, base, lg] = RADII[theme.corners];
        properties['--sone-radius-sm'] = sm;
        properties['--sone-radius'] = base;
        properties['--sone-radius-lg'] = lg;
    }
    for (const element of THEMED_ELEMENTS) {
        const entry = theme[element];
        if (!entry)
            continue;
        if (entry.size !== undefined) {
            // A step is a multiplier rather than a size, so it composes with the
            // reading scale instead of fighting it: somebody who reads at a larger
            // scale sees this workspace's proportions, larger.
            properties[`--sone-theme-${element}-size`] = `${(1.125 ** entry.size).toFixed(4)}`;
        }
        const color = colorValue(entry.color);
        if (color !== undefined) {
            properties[`--sone-theme-${element}-color`] = color;
        }
        if (entry.spaceAbove !== undefined) {
            properties[`--sone-theme-${element}-space-above`] = `${entry.spaceAbove}`;
        }
        if (entry.spaceBelow !== undefined) {
            properties[`--sone-theme-${element}-space-below`] = `${entry.spaceBelow}`;
        }
    }
    return properties;
}
// --- entry appearance -------------------------------------------------------
/**
 * The icons an entry may carry.
 *
 * Lucide, which is ISC-licensed line work that matches the rest of this
 * interface — the alternative first attempt was emoji, and it was the wrong
 * answer: emoji are somebody else's drawings in somebody else's style, they
 * differ on every platform, and a sidebar of them does not look designed.
 *
 * A curated list rather than the whole set. Two thousand icons is a search
 * problem rather than a choice, and a name stored here has to keep resolving —
 * a closed list is a promise that it will, and the names are checked against
 * what the interface actually renders.
 */
export const ENTRY_ICONS = [
    'folder',
    'folder-open',
    'file-text',
    'notebook',
    'book',
    'bookmark',
    'star',
    'heart',
    'flag',
    'target',
    'lightbulb',
    'rocket',
    'briefcase',
    'building',
    'home',
    'users',
    'user',
    'message-circle',
    'mail',
    'phone',
    'calendar',
    'clock',
    'check-circle',
    'list-todo',
    'chart-bar',
    'trending-up',
    'coins',
    'shopping-cart',
    'package',
    'truck',
    'wrench',
    'settings',
    'code',
    'database',
    'server',
    'cloud',
    'lock',
    'key',
    'shield',
    'archive',
    'camera',
    'image',
    'music',
    'video',
    'map-pin',
    'globe',
    'plane',
    'car',
    'leaf',
    'sun',
    // Widened after the first fifty turned out to lean heavily on office work.
    // A notes tool holds recipes, training plans, house repairs and holidays as
    // readily as it holds meetings.
    'moon',
    'cloud-rain',
    'flame',
    'droplet',
    'mountain',
    'tree-pine',
    'flower',
    'bug',
    'cat',
    'dog',
    'bird',
    'fish',
    'coffee',
    'utensils',
    'pizza',
    'cake',
    'apple',
    'wine',
    'shopping-bag',
    'gift',
    'shirt',
    'bed',
    'lamp',
    'sofa',
    'bath',
    'hammer',
    'paintbrush',
    'palette',
    'scissors',
    'ruler',
    'pen-tool',
    'guitar',
    'headphones',
    'film',
    'gamepad-2',
    'dice-5',
    'puzzle',
    'trophy',
    'medal',
    'dumbbell',
    'bike',
    'footprints',
    'tent',
    'anchor',
    'ship',
    'train-front',
    'bus',
    'fuel',
    'compass',
    'map',
    'luggage',
    'hospital',
    'pill',
    'stethoscope',
    'heart-pulse',
    'baby',
    'graduation-cap',
    'school',
    'library',
    'newspaper',
    'quote',
    'languages',
    'microscope',
    'atom',
    'flask-conical',
    'telescope',
    'sprout',
    'recycle',
    'battery-charging',
    'plug',
    'wifi',
    'smartphone',
    'laptop',
    'printer',
    'hard-drive',
    'terminal',
    'git-branch',
    'bug-play',
    'banknote',
    'credit-card',
    'receipt',
    'calculator',
    'scale',
    'gavel',
    'landmark',
    'church',
    'store',
    'factory',
    'warehouse',
    'sun-medium',
    'snowflake',
    'umbrella',
    'wind',
];
/**
 * Read an icon from whatever is stored, or null.
 *
 * Unknown shapes yield null rather than throwing: this comes out of a document
 * another client wrote, and an entry with a malformed icon should lose its icon
 * and not its place in the tree.
 */
export function readEntryIcon(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const raw = value;
    if (raw['kind'] !== 'icon')
        return null;
    // Checked by shape rather than against a list.
    //
    // It was a closed list, on the reasoning that a stored name has to keep
    // resolving. That reasoning was weaker than it sounded: the guarantee only
    // ever held for the icon set installed at the time, and an upgrade that
    // renames one breaks it either way. What actually protects an entry is the
    // interface drawing its default when a name does not resolve, which it does.
    //
    // So the shape is what is enforced — lower-case, digits and hyphens, bounded
    // — which keeps the value safe to put in a class name or a lookup, and lets
    // the picker offer every icon it has rather than the fifty somebody once
    // chose.
    const name = raw['value'];
    if (typeof name !== 'string' || !/^[a-z][a-z0-9-]{0,48}$/.test(name))
        return null;
    const color = readChosenColor(raw['color']);
    return {
        kind: 'icon',
        value: name,
        ...(color ? { color } : {}),
    };
}
/** A title colour, or null. Stored beside the icon, kept separate from it. */
export function readTitleColor(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    return readChosenColor(value['titleColor']);
}
/**
 * How wide a cover runs (ADR-0162).
 *
 * The **page's** two words rather than a block's three. A block offers a middle
 * step, and the block menu already refuses to offer it for an image, in these
 * words: *"all three read as the width of the text, or a bit more. An image is
 * either in the column with the writing or across the page."* A cover is that
 * picture, one line higher up.
 *
 * And a third step above `full` (ADR-0163): `bleed` is the whole page *and* up
 * under the top bar, so the picture starts at the edge of the window. A third
 * value in the same field rather than a second field, because it is the widest
 * of three steps and not a second question — a band in the reading column that
 * also ran under the bar would put the page's controls over the page's own
 * background, which is where they already are.
 */
export const COVER_WIDTHS = ['column', 'full', 'bleed'];
/**
 * How tall it is — *„schmal mittel und hoch oder so"*.
 *
 * Three named steps rather than a number, because this is a band across a page
 * and not a picture frame: a height somebody types is a height that can be
 * three pixels, and every value between the three is a decision nobody needs to
 * make.
 */
export const COVER_HEIGHTS = ['slim', 'medium', 'tall'];
/**
 * A file uploaded to this instance, and nothing else.
 *
 * Asked for in those words — *„nur eigene Bilder, kein Unsplash"* — and
 * enforced here rather than in the picker, because a cover is drawn on every
 * page load: a foreign URL in one is a page that reports every reader to
 * somebody else's server, and the document is written by clients rather than by
 * the picker alone.
 *
 * Anchored at both ends, so no `..` and no second path can ride along. The id
 * is a uuid because `files.id` is one; a value that cannot name a row is not a
 * picture, whatever it looks like.
 */
const FILE_URL = /^\/api\/files\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * Read a cover from whatever is stored, or null.
 *
 * Unknown and malformed shapes yield null rather than throwing, the rule
 * `readEntryIcon` states: an entry with a broken cover loses its cover and not
 * its place in the tree.
 */
export function readEntryCover(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const raw = value;
    /*
     * The shape, if it says anything about it (ADR-0162).
     *
     * A value nobody offers is **dropped rather than refused**: one level up, a
     * malformed cover loses its cover and not its place in the tree, so a cover
     * with a height nobody has heard of loses the height and not the picture.
     *
     * **And the defaults are dropped too.** `column` and `medium` are what a band
     * does when it says nothing, so they are never stored as words: two spellings
     * for one state is a control that has to decide which of them counts as
     * chosen, and a cover that reads as adjusted while looking exactly like every
     * cover nobody has touched. The vocabulary below still names all three
     * heights — one of them is simply written as silence.
     */
    const shape = {};
    if (raw['width'] === 'full' || raw['width'] === 'bleed')
        shape.width = raw['width'];
    if (COVER_HEIGHTS.includes(raw['height']) && raw['height'] !== 'medium') {
        shape.height = raw['height'];
    }
    if (raw['kind'] === 'image') {
        const url = raw['url'];
        if (typeof url !== 'string' || !FILE_URL.test(url))
            return null;
        return { kind: 'image', url, ...shape };
    }
    if (raw['kind'] === 'color') {
        const color = readChosenColor(raw['color']);
        return color ? { kind: 'color', color, ...shape } : null;
    }
    if (raw['kind'] === 'gradient') {
        // Both ends, or it is not a gradient. Filling the missing one in with a
        // default would put a colour on the page that nobody chose.
        const from = readChosenColor(raw['from']);
        const to = readChosenColor(raw['to']);
        return from && to ? { kind: 'gradient', from, to, ...shape } : null;
    }
    return null;
}
/**
 * What to paint behind the heading for a cover that is not a picture.
 *
 * Undefined for a picture, which is drawn as an `<img>` instead: a background
 * image means building a CSS string out of a value from the document, and the
 * escaping rules for that are their own subject, while an `<img src>` is
 * escaped by the framework, can carry alternative text and can be told to load
 * lazily.
 *
 * Here rather than in the component because a page and a folder both draw one,
 * and two drawing sites are two chances to answer differently — which is how
 * the same colour ends up looking like two colours.
 */
export function coverBackground(cover) {
    if (!cover)
        return undefined;
    if (cover.kind === 'color')
        return colorValue(cover.color);
    if (cover.kind === 'gradient') {
        const from = colorValue(cover.from);
        const to = colorValue(cover.to);
        // Both resolved or neither: half a gradient drawn against the page's own
        // background is a cover that looks like a rendering fault.
        if (!from || !to)
            return undefined;
        // Diagonal, so the two ends are both visible in a band this short.
        return `linear-gradient(150deg, ${from}, ${to})`;
    }
    return undefined;
}
//# sourceMappingURL=theme.js.map